/**
 * Coach-intelligence Rung 1 — the Today echo (§11).
 *
 * Fetches the LATEST eligible frozen reaction inside the retention window from
 * the server (`GET /api/runs/reaction/today`, self-only). The SERVER is the
 * authority (§12): it decides whether a reaction exists (freeze + windowing +
 * EC-0 + minor/unknown-age suppression). This hook FAILS CLOSED —
 *   - the client flag `coach_react_v1` is a SURFACE-READINESS signal only: when
 *     stale-off we skip the request (no request → no line), and even if it fired
 *     the server returns null when the server flag is off;
 *   - any fetch/parse error → null (no client-built line, ever).
 */
import { useEffect, useState } from 'react';
import { useAuthFetch } from './useAuthFetch';
import { useServerFlag } from './useServerFlag';
import type { CoachReaction } from '../lib/run/coachReaction';

/** The reaction and whether its first read is still open (W1-TODAY-FIX3). */
export interface CoachReactionTodayState {
  reaction: CoachReaction | null;
  /**
   * True from mount while `coach_react_v1` is on and the read has neither resolved nor
   * failed; false when the flag is off (no request is made) and once the first read has
   * settled, empty or not. It is a cold-read signal: a later re-read (a new authed fetch
   * after a client change) does not set it again, so a loaded page never blanks.
   */
  loading: boolean;
}

export function useCoachReactionTodayState(): CoachReactionTodayState {
  const flagOn = useServerFlag('coach_react_v1');
  const fetchAuthed = useAuthFetch();
  const [reaction, setReaction] = useState<CoachReaction | null>(null);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    // Surface-readiness gate — fail closed to "no line" when the flag is off.
    if (!flagOn) {
      setReaction(null);
      return;
    }
    let alive = true;
    fetchAuthed('/api/runs/reaction/today')
      .then((res) => (res.ok ? (res.json() as Promise<{ reaction?: CoachReaction | null }>) : null))
      .then((body) => {
        if (alive) {
          setReaction(body?.reaction ?? null);
          setSettled(true);
        }
      })
      .catch(() => {
        if (alive) {
          setReaction(null);
          setSettled(true);
        }
      });
    return () => {
      alive = false;
    };
  }, [flagOn, fetchAuthed]);

  return { reaction, loading: flagOn && !settled };
}

export function useCoachReactionToday(): CoachReaction | null {
  return useCoachReactionTodayState().reaction;
}
